import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import '../styles/ProjectRoom.css';

const ProjectRoom = ({ project: initialProject, user }) => {
    const [project, setProject] = useState(initialProject);
    const [message, setMessage] = useState('');
    const chatEndRef = useRef(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const renderAvatar = (userObject, className) => {
        if (userObject?.profile_pic && userObject.profile_pic !== '/uploads/default.png') {
            return (
                <img
                    src={`${userObject.profile_pic}`}
                    className={className}
                    alt="pfp"
                    style={{ objectFit: 'cover' }}
                />
            );
        }
        return (
            <div className={`${className} fallback-avatar`} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#00cec9',
                color: 'white',
                fontWeight: 'bold',
                borderRadius: '50%'
            }}>
                {userObject?.username?.charAt(0).toUpperCase()}
            </div>
        );
    };

    useEffect(() => {
        const fetchRoomData = async () => {
            if (!initialProject?.id) return;
            try {
                const token = sessionStorage.getItem('token');
                const res = await api.get(`/api/project-room/${initialProject.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProject(res.data);
            } catch (err) {
                console.error("Error loading room data:", err);
            }
        };
        fetchRoomData();
    }, [initialProject.id]);

    useEffect(scrollToBottom, [project?.messages]);

    const handleLeaveProject = async () => {
        const confirmLeave = window.confirm("Are you sure you want to leave this project?");
        if (!confirmLeave) return;

        try {
            const token = sessionStorage.getItem('token');
            await api.delete(`/api/project-room/${project.id}/leave`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Redirects to dashboard after successfully leaving
            navigate('/dashboard'); 
        } catch (err) {
            console.error("Error leaving project:", err);
            alert(err.response?.data?.error || "Failed to leave project");
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        try {
            const token = sessionStorage.getItem('token');
            const res = await api.post(`/api/project-room/${initialProject.id}/messages`,
                { content: message },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setProject(prev => ({
                ...prev,
                messages: [...prev.messages, res.data]
            }));
            setMessage('');
        } catch (err) {
            console.error("Error sending message:", err);
        }
    };

    if (!project) return <div className="loading-screen">Loading Project Room...</div>;

    return (
        <div className="project-room-container">
            <div className="top-section">
                <div className="info-panel">
                    <img
                        src={`${project.image || project.image_url}`}
                        alt="Project"
                        className="project-main-img"
                    />
                    <div className="project-text-details">
                        <div className="project-header-flex">
                            <h1>{project.title || project.name}</h1>
                            
                            {project.creator_id !== user.user_id && (
                                <button className="leave-project-btn" onClick={handleLeaveProject}>
                                    Leave Project
                                </button>
                            )}
                        </div>

                        <div className="project-meta-info">
                            <div className="project-category-text">{project.category}</div>
                            <div className="project-simple-code">
                                Code: <span>{project.code}</span>
                            </div>
                        </div>

                        <p className="project-description-text">{project.description}</p>
                    </div>
                </div>

                <div className="members-panel">
                    <h3>Project Members</h3>
                    <div className="members-list-scroll">
                        {project.members?.map((member, index) => (
                            <div key={index} className="member-card">
                                {renderAvatar(member, "member-card-img")}
                                <span>{member.username}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bottom-section">
                <div className="chat-section">
                    <div className="messages-window">
                        {project.messages?.map((msg, index) => (
                            <div key={index} className={`message-row ${msg.username === user.username ? 'is-me' : 'is-other'}`}>
                                {renderAvatar(msg, "chat-pfp")}
                                <div className="msg-content-wrapper">
                                    <span className="sender-label">{msg.username}</span>
                                    <div className="msg-bubble">
                                        {msg.content}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={chatEndRef} />
                    </div>

                    <form className="chat-input-wrapper" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            placeholder="Type a message..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                        <button type="submit" className="send-arrow-btn">
                            <span>➤</span>
                        </button>
                    </form>
                </div>

                <div className="files-panel">
                    <h3>Images and Files</h3>
                    <div className="files-placeholder">
                        <span>No files uploaded yet</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectRoom;